import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import TalentTree from './TalentTree';
import './talents.css';

const ICON_URL = (icon) => `https://wow.zamimg.com/images/wow/icons/medium/${icon}.jpg`;

// Caché en memoria por personaje: volver a la pestaña no repite la petición
const cache = new Map();

function GlyphList({ glyphs, onHover }) {
  const groups = [
    { type: 'major', title: 'Glifos sublimes' },
    { type: 'minor', title: 'Glifos menores' },
  ];
  return (
    <div className="talents-glyphs" data-testid="talents-glyphs">
      {groups.map(({ type, title }) => {
        const list = glyphs.filter((g) => g.type === type);
        return (
          <div key={type} className="talents-glyph-group">
            <h4 className="talents-glyph-title">{title}</h4>
            {list.length === 0 && <p className="talents-glyph-empty">Ninguno</p>}
            {list.map((g) => (
              <div
                key={g.id}
                className={`talents-glyph talents-glyph--${type}`}
                data-testid={`glyph-${g.id}`}
                onMouseEnter={(e) => onHover({ glyph: g, el: e.currentTarget })}
                onMouseLeave={() => onHover(null)}
              >
                <img src={ICON_URL(g.icon)} alt="" className="talents-glyph-icon" loading="lazy" />
                <span className="talents-glyph-name">{g.name}</span>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

// En un portal sobre <body>: el backdrop-filter/transform del panel crea un bloque
// contenedor y descoloca cualquier position: fixed que viva dentro.
function Tooltip({ hover }) {
  if (!hover) return null;
  return createPortal(<TooltipBox hover={hover} />, document.body);
}

const TOOLTIP_W = 320;

function TooltipBox({ hover }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ left: -9999, top: 0 });

  // A la derecha del icono (a la izquierda si no cabe) y sin salirse por abajo
  useLayoutEffect(() => {
    const rect = hover.el.getBoundingClientRect();
    const h = ref.current?.offsetHeight ?? 0;
    const left = rect.right + 8 + TOOLTIP_W > window.innerWidth
      ? Math.max(8, rect.left - 8 - TOOLTIP_W)
      : rect.right + 8;
    const top = Math.max(8, Math.min(rect.top, window.innerHeight - h - 8));
    setPos({ left, top });
  }, [hover]);

  return (
    <div ref={ref} className="talents-tooltip" style={{ ...pos, width: TOOLTIP_W }} data-testid="talents-tooltip">
      {hover.glyph ? <GlyphTooltip glyph={hover.glyph} /> : <TalentTooltip {...hover} />}
    </div>
  );
}

function Desc({ text }) {
  return <p className="talents-tooltip-desc">{text}</p>;
}

function TalentTooltip({ talent, rank, requirements }) {
  const max = talent.ranks.length;
  const current = talent.desc[Math.max(0, rank - 1)];
  const next = rank > 0 && rank < max ? talent.desc[rank] : null;
  const cast = talent.cast;
  return (
    <>
      <div className="talents-tooltip-name">{talent.name}</div>
      <div className="talents-tooltip-line">Rango {rank}/{max}</div>
      {requirements.map((r) => (
        <div key={r} className="talents-tooltip-req">{r}</div>
      ))}
      {cast && (
        <div className="talents-tooltip-cast">
          <span>{cast.cost ?? ''}</span><span>{cast.range ?? ''}</span>
          <span>{cast.cast ?? ''}</span><span>{cast.cooldown ?? ''}</span>
        </div>
      )}
      <Desc text={current} />
      {next && (
        <>
          <div className="talents-tooltip-line talents-tooltip-next">Siguiente rango:</div>
          <Desc text={next} />
        </>
      )}
    </>
  );
}

function GlyphTooltip({ glyph }) {
  return (
    <>
      <div className="talents-tooltip-name">{glyph.name}</div>
      <div className="talents-tooltip-line talents-tooltip-glyph-type">
        {glyph.type === 'major' ? 'Glifo sublime' : 'Glifo menor'}
      </div>
      <Desc text={glyph.desc} />
    </>
  );
}

export default function TalentsPanel({ backendUrl, name }) {
  const [data, setData] = useState(() => cache.get(name) ?? null);
  const [error, setError] = useState(null);
  const [spec, setSpec] = useState(() => cache.get(name)?.active_spec ?? 0);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    if (cache.has(name)) return;
    let cancelled = false;
    axios.get(`${backendUrl}/api/armory/character/${encodeURIComponent(name)}/talents`)
      .then(({ data: d }) => {
        cache.set(name, d);
        if (cancelled) return;
        setData(d);
        setSpec(d.active_spec);
      })
      .catch(() => { if (!cancelled) setError('No se pudieron cargar los talentos.'); });
    return () => { cancelled = true; };
  }, [backendUrl, name]);

  // El tooltip usa posición fija: se oculta al hacer scroll para no quedarse descolocado
  useEffect(() => {
    const hide = () => setHover(null);
    window.addEventListener('scroll', hide, true);
    return () => window.removeEventListener('scroll', hide, true);
  }, []);

  if (error) return <div className="charprofile-error" data-testid="talents-error">{error}</div>;
  if (!data) return <div className="charprofile-loading" data-testid="talents-loading">Consultando el grimorio...</div>;

  const current = data.specs.find((s) => s.index === spec) ?? data.specs[0];

  return (
    <div className="talents-panel" data-testid="talents-panel">
      <div className="talents-spec-bar">
        {data.specs.length > 1 && (
          <div className="talents-spec-tabs" role="tablist">
            {data.specs.map((s) => (
              <button
                key={s.index}
                role="tab"
                aria-selected={s.index === current.index}
                className={`talents-spec-tab ${s.index === current.index ? 'talents-spec-tab--active' : ''}`}
                data-testid={`talents-spec-tab-${s.index}`}
                onClick={() => { setSpec(s.index); setHover(null); }}
              >
                {s.index === 0 ? 'Especialización principal' : 'Especialización secundaria'}
                {s.index === data.active_spec && <span className="talents-spec-active">activa</span>}
              </button>
            ))}
          </div>
        )}
        <div className="talents-spec-summary" data-testid="talents-summary">
          {current.points.join(' / ')}
        </div>
      </div>

      <div className="talents-trees">
        {data.trees.map((tree, i) => (
          <TalentTree
            key={tree.id}
            tree={tree}
            ranks={current.talents}
            points={current.points[i] ?? 0}
            onHover={setHover}
          />
        ))}
        <GlyphList glyphs={current.glyphs} onHover={setHover} />
      </div>

      <Tooltip hover={hover} />
    </div>
  );
}
